import type { TagVariant } from "../../../components/ui/Tag";
import type { Api } from "../../../services/api";
import i18n from "../../../config/i18n";

const t = i18n.t;

// The design vocabulary, inferred from the endpoint that serves it rather than
// restated here: a design added to `ROB_DESIGNS` in the api then fails the
// exhaustive switch below instead of silently rendering nothing.
type RobDesign = Extract<
  NonNullable<
    Api["get"]["/webapp/private/works/risk-of-bias"]["output"]["body"][number]["method"]
  >,
  { state: "appraised" }
>["design"];

// Display config per canonical article type. Colors are grouped by semantic
// family so related types share a hue: empirical studies (mint), case/short
// reports (lime), evidence synthesis (blueberry), normative/protocol (pine),
// opinion/editorial (clementine). `text` is the translated label — each t()
// call takes a string literal so the i18n scanner can extract the key. Unknown
// / legacy values fall back to the neutral slate chip and show the raw value.
// Keyed on the exact article_type string the API returns (the canonical set in
// apps/api ARTICLE_TYPES) — no lossy normalization.
function articleTypeConfig(article_type: string): {
  text: string;
  variant: TagVariant;
} {
  switch (article_type) {
    case "Case Report":
      return { text: t("Case Report"), variant: "lime" };
    case "Short communication":
      return { text: t("Short Communication"), variant: "lime" };
    // The RCT family is abbreviated on the webapp only (canonical/admin keep the
    // full name). The `t()` key is the EN short form; each locale maps it to its
    // own medical acronym (ECR/ECA) — or, for Arabic, the full spelled-out form,
    // since a Latin acronym would clash with the RTL script.
    case "Randomized Controlled Trial":
      return { text: t("RCT"), variant: "mint" };
    case "Randomized Controlled Trial, Phase I":
      return { text: t("RCT, Phase I"), variant: "mint" };
    case "Randomized Controlled Trial, Phase II":
      return { text: t("RCT, Phase II"), variant: "mint" };
    case "Randomized Controlled Trial, Phase III":
      return { text: t("RCT, Phase III"), variant: "mint" };
    case "Randomized Controlled Trial, Phase IV":
      return { text: t("RCT, Phase IV"), variant: "mint" };
    case "Clinical Trial":
      return { text: t("Clinical Trial"), variant: "mint" };
    case "Clinical Trial, Phase I":
      return { text: t("Clinical Trial, Phase I"), variant: "mint" };
    case "Clinical Trial, Phase II":
      return { text: t("Clinical Trial, Phase II"), variant: "mint" };
    case "Clinical Trial, Phase III":
      return { text: t("Clinical Trial, Phase III"), variant: "mint" };
    case "Clinical Trial, Phase IV":
      return { text: t("Clinical Trial, Phase IV"), variant: "mint" };
    case "Observational Study":
      return { text: t("Observational Study"), variant: "mint" };
    case "Clinical study":
      return { text: t("Clinical Study"), variant: "mint" };
    case "Original research":
      return { text: t("Original Research"), variant: "mint" };
    case "Meta-analysis":
      return { text: t("Meta-Analysis"), variant: "blueberry" };
    case "Systematic review and meta-analysis":
      return {
        text: t("Systematic Review and Meta-Analysis"),
        variant: "blueberry",
      };
    case "Systematic Review":
      return { text: t("Systematic Review"), variant: "blueberry" };
    case "Review":
      return { text: t("Review"), variant: "blueberry" };
    case "Guidelines / Consensus":
      return { text: t("Guidelines / Consensus"), variant: "pine" };
    case "Study Protocol":
      return { text: t("Study Protocol"), variant: "pine" };
    case "Technical report":
      return { text: t("Technical Report"), variant: "pine" };
    case "Editorial":
      return { text: t("Editorial"), variant: "clementine" };
    case "Letter":
      return { text: t("Letter"), variant: "clementine" };
    case "Commentary":
      return { text: t("Commentary"), variant: "clementine" };
    case "Perspective":
      return { text: t("Perspective"), variant: "clementine" };
    default:
      return { text: article_type, variant: "slate" };
  }
}

export function getArticleTypeVariant(
  article_type: string | null | undefined
): TagVariant {
  if (article_type === null || article_type === undefined) return "slate";
  return articleTypeConfig(article_type).variant;
}

export function getArticleTypeText(article_type: string | null | undefined) {
  if (article_type === null || article_type === undefined)
    return t("Clinical Study");
  return articleTypeConfig(article_type).text;
}

// The finer study design, for the observational bucket the canonical taxonomy
// collapses into one "Observational Study" chip. Recovered by the risk-of-bias
// design resolver, so the vocabulary is `ROB_DESIGNS` rather than ARTICLE_TYPES.
//
// The parameter is named `t` (shadowing the module singleton above) because the
// i18n scanner extracts keys from call sites named exactly that. A
// differently-named parameter makes these nine labels invisible to it, and
// `pnpm i18n:scan` then prunes them from all eight locales. The scanner reads
// comments as well as code, so this one states the rule without writing the
// call shape it describes.
//
// Takes `t` rather than reading the singleton, because the two callers differ:
// a component inside `useTranslation()` re-renders on a language change and must
// pass its own `t`, while `getStudyDesignText` below serves the callers that
// build a row outside React. One table either way — the sources list of the
// general chat and the sources list of Evidence GPT draw the same word for the
// same design, which they did not before.
//
// The article page's evidence ledger (`WorksScoreScreen`) keeps its own wording
// on purpose: "Systematic review or meta-analysis" and "Non-randomized trial"
// are the signed-off phrasing of that surface, not a drifted copy of this one.
export function studyDesignLabel(
  t: (key: string) => string,
  design: RobDesign
): string {
  switch (design) {
    case "randomized_trial":
      return t("Randomized trial");
    case "non_randomized_interventional":
      return t("Non-randomized interventional study");
    case "cohort":
      return t("Cohort study");
    case "case_control":
      return t("Case-control study");
    case "cross_sectional":
      return t("Cross-sectional study");
    case "diagnostic_accuracy":
      return t("Diagnostic accuracy study");
    case "systematic_review":
      return t("Systematic review");
    case "qualitative":
      return t("Qualitative study");
    case "prognostic":
      return t("Prognostic study");
    default:
      throw new Error(design satisfies never);
  }
}

/** The same table, for a caller that builds a row outside a React component. */
export function getStudyDesignText(design: RobDesign): string {
  return studyDesignLabel(t, design);
}
